import { gql } from "@apollo/client";

export const GET_ALL_GISTS = gql`
  query GetGists {
    gists {
      id
      title
      parentId
      subjectId
      status
      publishedAt
      canEditInPlace
      canMoveToDraft
      canPublish
      lockReasons
      createdAt
      versions {
        id
        point
        gistId
        user {
          name
        }
        createdAt
        edits {
          id
          body
          versionId
          user {
            id
            name
            image
          }
          newnessCount
          importantCount
          qualityCount
          flag
          comments {
            id
            comment
            parentId
            userId
            gistId
            editId
            isRemoved
            removedAt
            removedById
            removedByRole
            removedReason
            user {
              id
              name
            }
            createdAt
          }
          createdAt
        }
      }
    }
  }
`;

export const GET_GISTS_BY_SUBJECT = gql`
  query GistsBySubject($subjectId: Int!) {
    gistsBySubject(subjectId: $subjectId) {
      id
      title
      parentId
      subjectId
      status
      publishedAt
      canEditInPlace
      canMoveToDraft
      canPublish
      lockReasons
      createdAt
      versions {
        id
        point
        gistId
        user {
          id
          name
          image
        }
        createdAt
        edits {
          id
          body
          versionId
          user {
            id
            name
            image
          }
          newnessCount
          importantCount
          qualityCount
          flag
          comments {
            id
            comment
            parentId
            userId
            gistId
            editId
            isRemoved
            removedAt
            removedById
            removedByRole
            removedReason
            user {
              id
              name
            }
            createdAt
          }
          createdAt
        }
      }
    }
  }
`;

export const GET_GISTS_BY_USER = gql`
  query GistsByUser($userId: Int!) {
    gistsByUser(userId: $userId) {
      id
      title
      parentId
      subjectId
      status
      publishedAt
      canEditInPlace
      canMoveToDraft
      canPublish
      lockReasons
      createdAt
      versions {
        id
        point
        gistId
        user {
          id
          name
          image
        }
        createdAt
        edits {
          id
          body
          versionId
          user {
            id
            name
            image
          }
          newnessCount
          importantCount
          qualityCount
          flag
          comments {
            id
            comment
            parentId
            userId
            gistId
            editId
            isRemoved
            removedAt
            removedById
            removedByRole
            removedReason
            user {
              id
              name
            }
            createdAt
          }
          createdAt
        }
      }
    }
  }
`;

export const CREATE_GIST = gql`
  mutation AddGist($gist: AddGistInput) {
    addGist(gist: $gist) {
      id
    }
  }
`;

/**
 * Preferred mutation for all new gist creation.
 * The server runs Gist + Version + Edit inside one Prisma transaction,
 * so partial failures are impossible.
 */
export const CREATE_GIST_WITH_CONTENT = gql`
  mutation CreateGistWithContent($input: CreateGistWithContentInput!) {
    createGistWithContent(input: $input) {
      id
      title
      subjectId
      parentId
      status
      publishedAt
      canEditInPlace
      canMoveToDraft
      canPublish
      lockReasons
      createdAt
    }
  }
`;

export const DELETE_GIST = gql`
  mutation deleteGist($id: Int!) {
    deleteGist(id: $id) {
      success
      message
    }
  }
`;

export const UPDATE_GIST_IN_PLACE = gql`
  mutation UpdateGistInPlace($input: UpdateGistInPlaceInput!) {
    updateGistInPlace(input: $input) {
      id
      title
      status
      publishedAt
      canEditInPlace
      canMoveToDraft
      canPublish
      lockReasons
    }
  }
`;

export const PUBLISH_GIST = gql`
  mutation PublishGist($id: Int!) {
    publishGist(id: $id) {
      id
      status
      publishedAt
      canEditInPlace
      canMoveToDraft
      canPublish
      lockReasons
    }
  }
`;

export const MOVE_GIST_TO_DRAFT = gql`
  mutation MoveGistToDraft($id: Int!) {
    moveGistToDraft(id: $id) {
      id
      status
      publishedAt
      canEditInPlace
      canMoveToDraft
      canPublish
      lockReasons
    }
  }
`;

export const DISCARD_EDIT = gql`
  mutation DiscardEdit($id: Int!) {
    discardEdit(id: $id) {
      success
      message
    }
  }
`;

export const GET_ALL_SUBJECTS = gql`
  query Subjects {
    subjects {
      id
      title
      parentId
    }
  }
`;

export const CREATE_SUBJECT = gql`
  mutation AddSubject($subject: AddSubjectInput) {
    addSubject(subject: $subject) {
      id
      title
      parentId
      userId
    }
  }
`;

export const UPDATE_SUBJECT = gql`
  mutation updateSubject($id: Int!, $subject: UpdateSubjectInput!) {
    updateSubject(id: $id, subject: $subject) {
      title
      parentId
    }
  }
`;

export const DELETE_SUBJECT = gql`
  mutation deleteSubject($id: Int!) {
    deleteSubject(id: $id) {
      success
      message
    }
  }
`;

export const CREATE_VERSION = gql`
  mutation AddVersion($version: AddVersionInput) {
    addVersion(version: $version) {
      id
    }
  }
`;

export const DELETE_VERSION = gql`
  mutation deleteVersion($id: Int!) {
    deleteVersion(id: $id) {
      id
    }
  }
`;

export const CREATE_EDIT = gql`
  mutation addEdit($edit: AddEditInput) {
    addEdit(edit: $edit) {
      body
      id
    }
  }
`;

export const UPDATE_EDIT = gql`
  mutation updateEdit($id: Int!, $edit: UpdateEditInput!) {
    updateEdit(id: $id, edit: $edit) {
      flag
      newnessCount
      importantCount
      qualityCount
    }
  }
`;

export const CREATE_ACTION = gql`
  mutation addUserEditAction($action: AddUserEditActionInput) {
    addUserEditAction(action: $action) {
      id
      editId
      field
      actionType
    }
  }
`;

export const CREATE_FAVORITE = gql`
  mutation addFavorite($fav: AddFavoriteInput) {
    addFavorite(fav: $fav) {
      id
      gistId
      editId
    }
  }
`;

export const GET_FAVORITE = gql`
  query Favorite($userId: Int!, $editId: Int!) {
    favorite(userId: $userId, editId: $editId) {
      id
    }
  }
`;

export const CREATE_COMMENT = gql`
  mutation addComment($newComment: AddCommentInput) {
    addComment(comment: $newComment) {
      id
      comment
      parentId
      userId
      gistId
      editId
      isRemoved
      removedAt
      removedById
      removedByRole
      removedReason
    }
  }
`;

export const SOFT_REMOVE_COMMENT = gql`
  mutation SoftRemoveComment($id: Int!) {
    softRemoveComment(id: $id) {
      id
      comment
      parentId
      userId
      gistId
      editId
      isRemoved
      removedAt
      removedById
      removedByRole
      removedReason
      updatedAt
    }
  }
`;

export const DELETE_COMMENT_THREAD = gql`
  mutation DeleteCommentThread($id: Int!) {
    deleteCommentThread(id: $id) {
      success
      message
    }
  }
`;

// Backward-compatible export name for older imports.
// New UI code should use DELETE_COMMENT_THREAD because this is a hard delete.
export const DELETE_COMMENT = DELETE_COMMENT_THREAD;

export const GET_COMMENT = gql`
  query comments($gistId: Int, $editId: Int) {
    comments(gistId: $gistId, editId: $editId) {
      id
      comment
      parentId
      userId
      gistId
      editId
      isRemoved
      removedAt
      removedById
      removedByRole
      removedReason
      user {
        id
        name
        image
      }
      createdAt
      replies {
        id
        comment
        parentId
        userId
        gistId
        editId
        isRemoved
        removedAt
        removedById
        removedByRole
        removedReason
        user {
          id
          name
          image
        }
        createdAt
        replies {
          id
          comment
          parentId
          userId
          gistId
          editId
          isRemoved
          removedAt
          removedById
          removedByRole
          removedReason
          user {
            id
            name
            image
          }
          createdAt
          replies {
            id
            comment
            parentId
            userId
            gistId
            editId
            isRemoved
            removedAt
            removedById
            removedByRole
            removedReason
            user {
              id
              name
              image
            }
            createdAt
          }
        }
      }
    }
  }
`;
